import { createElementVNode as _createElementVNode, unref as _unref, toDisplayString as _toDisplayString, createVNode as _createVNode, withCtx as _withCtx, resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode } from "vue";
const _hoisted_1 = {
  class: "revenue-allocation-tab space-y-6 pt-4"
};
const _hoisted_2 = {
  key: 0,
  class: "flex gap-12 items-start"
};
const _hoisted_3 = {
  class: "flex flex-col gap-2 w-3/12"
};
const _hoisted_4 = {
  class: "flex items-center gap-2 px-4 py-2 border border-gray-300 rounded bg-white min-w-56"
};
const _hoisted_5 = {
  class: "text-base font-semibold text-gray-900"
};
const _hoisted_6 = {
  class: "flex flex-col gap-1 w-9/12"
};
const _hoisted_7 = {
  class: "flex items-center gap-2 position-relative"
};
const _hoisted_8 = {
  key: 1,
  class: "flex gap-12 items-start"
};
const _hoisted_9 = {
  class: "flex flex-col gap-2 w-3/12"
};
const _hoisted_10 = {
  class: "flex items-center gap-2 px-4 py-2 border border-gray-300 rounded bg-white min-w-56"
};
const _hoisted_11 = {
  class: "text-base font-semibold text-gray-900"
};
const _hoisted_12 = {
  key: 2,
  class: "p-1 text-sm flex items-start gap-2",
  role: "status",
  "data-cy": "allocation-state-message"
};
const _hoisted_13 = {
  key: 3,
  class: "flex gap-3 justify-end mt-0"
};
const _hoisted_14 = {
  key: 2,
  class: "w-56"
};
const _hoisted_15 = ["data-cy"];
const _hoisted_16 = {
  class: "flex items-start gap-3"
};
const _hoisted_17 = {
  class: "flex-1"
};
const _hoisted_18 = {
  class: "text-sm text-blue-900"
};
const _hoisted_19 = {
  key: 0,
  class: "block mt-2"
};
const _hoisted_20 = {
  key: 5,
  class: "text-sm text-blue-900 mb-4",
  "data-cy": "allocation-ac10-inline-link"
};
const _hoisted_21 = {
  key: 6,
  class: "space-y-4",
  "data-cy": "allocation-table-container"
};
const _hoisted_22 = {
  key: 0
};
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from 'vue';
import { storeToRefs } from 'pinia';
import { useRevenueAllocationStore } from '@/stores/useRevenueAllocationStore';
import { useCompanyStore } from '@/services/UserService/useCompanyStore';
import { formatCurrency } from '@/services/utils';
import { useAllocationCalculations } from '@/composables/useAllocationCalculations';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';
import LlConfirmationDialog from '@/components/LlConfirmationDialog.vue';
import LlTooltip from '@/components/LlTooltip.vue';
import SelectUser from '@/components/SelectUser.vue';
import AllocationTable from '../components/AllocationTable.vue';
export default {
  __name: 'RevenueAllocationTabV2',
  props: {
    chargeId: {
      type: String,
      required: true
    },
    users: {
      type: Array,
      default: () => []
    },
    fixedFeeAmount: {
      type: Number,
      default: 0
    },
    allocations: {
      type: Array,
      required: true
    },
    invoiceAccount: {
      type: [String, Object],
      default: null
    },
    state: {
      type: Object,
      default: null
    }
  },
  emits: ['saved', 'cancelled', 'validation-changed', 'update:allocations', 'update:invoiceAccount'],
  setup(__props, {
    expose: __expose,
    emit: __emit
  }) {
    const props = __props;
    const emit = __emit;

    // Store
    const store = useRevenueAllocationStore();
    const companyStore = useCompanyStore();
    const {
      allocations,
      isReadOnly,
      loading,
      isBilled,
      hasTimeEntries
    } = storeToRefs(store);
    const {
      companySettings
    } = storeToRefs(companyStore);

    // Use centralized allocation calculations composable
    const {
      distributeEvenly,
      roundToDecimals,
      roundPercentageForStorage
    } = useAllocationCalculations();

    // Local state
    const isSaving = ref(false);
    const modeChangeDialog = ref(null);
    const dialogVersion = ref(0);
    const radioVersion = ref(0);
    const previousMode = ref(null);
    const selectedMode = ref(null);
    const savedPercentageAllocations = ref(null);
    const selectedUserForAdd = ref(null);
    // VAutocomplete keeps the picked title as its search text, so clearing the
    // v-model alone leaves the name in the field; remounting resets it.
    const addUserPickerKey = ref(0);
    const isMounted = ref(false);
    const isInitialized = ref(false);
    const lastInvoiceAccount = ref(null);

    // Ensure allocations is always an array
    const allocationsArray = computed(() => {
      if (Array.isArray(allocations.value)) {
        return allocations.value;
      }
      return [];
    });

    // Optimized key for AllocationTable to force re-render only when necessary
    const allocationTableKey = computed(() => {
      const mainAccountIds = allocationsArray.value.filter(a => a.main).map(a => a.accountId).join(',');
      return `allocation-table-${store.allocationMode}-${allocationsArray.value.length}-${mainAccountIds}`;
    });

    // Available users for adding (exclude already allocated users)
    const availableUsers = computed(() => {
      if (!props.users) return [];
      if (!Array.isArray(store.allocations)) return props.users;
      return props.users.filter(user => !store.allocations.some(a => a.accountId === user.id));
    });

    // Show table if:
    // 1. In percentage mode (always can add/edit), or
    // 2. In read-only mode with allocations, or
    // 3. Has time entries (data available)
    // AC10: Check if "All services go to responsible attorney" setting is enabled
    const isServicesToResponsible = computed(() => {
      return !!companySettings.value?.report?.servicesToResponsible;
    });

    // Show table based on mode, allocations, and charge state
    // AC8: Don't show table if unbilled with NO time entries in percentage mode
    // AC9: Don't show table if billed with NO time entries in percentage mode
    // AC10: Don't show controls if all services go to responsible attorney
    const shouldShowTable = computed(() => {
      // AC10: If all services go to responsible attorney, hide controls
      if (isServicesToResponsible.value) {
        return false;
      }

      // In percentage mode, always show table (users can set up allocations even before time is tracked)
      if (store.allocationMode === store.ALLOCATION_MODES.PERCENTAGE) {
        return true;
      }

      // In non-percentage modes, always show table (to display the mode-specific message and allow data loading)
      return true;
    });

    // Show add user button only in percentage mode
    const canAddUsers = computed(() => {
      return store.allocationMode === store.ALLOCATION_MODES.PERCENTAGE;
    });

    // State-based message based on allocation mode and charge state
    const stateMessage = computed(() => {
      // AC10: All services go to responsible attorney
      if (isServicesToResponsible.value) {
        return 'All services are allocated to the responsible attorney.';
      }

      // // AC7: Unbilled with time entries - show preview message
      // if (!isBilled.value && hasTimeEntries.value && store.allocationMode === store.ALLOCATION_MODES.PERCENTAGE) {
      //   return 'This is a preview of the allocation based on current work. The allocation will be finalized when fixed fee is invoiced.';
      // }

      // AC8: Unbilled with NO time entries - show allocation will be based on time tracked
      if (!isBilled.value && !hasTimeEntries.value && store.allocationMode !== store.ALLOCATION_MODES.PERCENTAGE) {
        return 'Allocation will be based on time tracked against the fee.';
      }

      // AC9: Billed with NO time entries - show default settings message (only if no allocations exist)
      if (isBilled.value && !hasTimeEntries.value && store.allocationMode !== store.ALLOCATION_MODES.PERCENTAGE) {
        return 'There was no time tracked against the fixed fee, allocation will use default settings.';
      }

      // For Worked Hours mode
      if (store.allocationMode === store.ALLOCATION_MODES.WORKED_HOURS) {
        return 'Allocation calculated from worked hours and rates';
      }

      // For Worked Amount mode
      if (store.allocationMode === store.ALLOCATION_MODES.WORKED_AMOUNT) {
        return 'Allocation calculated from worked amount';
      }
      return '';
    });

    // Lifecycle
    onMounted(async () => {
      isMounted.value = true;

      // Initialize selectedMode with current store mode
      selectedMode.value = store.allocationMode;
      const isNewCharge = props.chargeId == 'new';

      // For new charges, use company-wide default allocation mode if configured
      if (isNewCharge) {
        const companyDefaultMode = companySettings.value?.fixedFeeAllocationMode;
        if (companyDefaultMode) {
          const numericMode = store.ALLOCATION_MODE_FROM_API[companyDefaultMode] ?? store.ALLOCATION_MODES.PERCENTAGE;
          selectedMode.value = numericMode;
          store.setAllocationMode(numericMode);
        }
      }

      // === STEP 1: Load or initialize allocations data ===
      const isPercentageMode = store.allocationMode === store.ALLOCATION_MODES.PERCENTAGE;
      const hasPropsAllocations = Array.isArray(props.allocations) && props.allocations.length > 0;
      if (!isNewCharge && !isPercentageMode) {
        // Existing charge in non-percentage mode: Load from API
        await store.loadAllocationsForMode(props.chargeId, store.allocationMode, props.users, props.fixedFeeAmount ?? null);
      } else if (!isNewCharge && isPercentageMode) {
        // Existing charge in percentage mode: Load from API
        await store.loadAllocationsForMode(props.chargeId, store.allocationMode, props.users, props.fixedFeeAmount ?? null);
      } else if (isNewCharge && isPercentageMode && hasPropsAllocations) {
        // NEW charge in percentage mode with props data: Use props allocations
        const normalizedAllocations = props.allocations.map(alloc => {
          if (alloc.account) {
            return {
              accountId: alloc.account.id || alloc.id,
              accountName: alloc.account.name,
              amount: alloc.amount || 0,
              percentage: alloc.percentage || 0,
              workedHours: alloc.workedHours || 0,
              workedAmount: alloc.workedAmount || 0,
              main: alloc.main || false
            };
          }
          return alloc;
        });
        store.updateAllocations([...normalizedAllocations]);
        savedPercentageAllocations.value = [...normalizedAllocations];
      } else if (isPercentageMode && !hasPropsAllocations && props.invoiceAccount) {
        // New charge in percentage mode with invoice account but no allocations
        const invoiceAccountId = typeof props.invoiceAccount === 'string' ? props.invoiceAccount : props.invoiceAccount.id;
        const user = props.users.find(u => u.id === invoiceAccountId);
        if (user) {
          const chargeAmount = props.fixedFeeAmount || store.chargeAmount;
          const newAllocation = {
            accountId: user.id,
            accountName: user.name || user.displayName,
            amount: chargeAmount,
            percentage: 100,
            workedHours: 0,
            workedAmount: 0,
            main: true
          };
          store.updateAllocations([newAllocation]);
          savedPercentageAllocations.value = [newAllocation];
        }
      }

      // === STEP 2: Sync invoice user with allocations ===
      await syncAllocationsWithInvoiceUser();

      // === STEP 3: Mark as initialized and emit to parent ===
      // Emit initial validation state
      emit('validation-changed', store.isAllocationValid);
      isInitialized.value = true;
      // Store normalized ID for comparison (handle both object and string)
      lastInvoiceAccount.value = typeof props.invoiceAccount === 'string' ? props.invoiceAccount : props.invoiceAccount?.id || null;
    });
    onBeforeUnmount(() => {
      isMounted.value = false;
    });

    // Safe emit helper to prevent emissions after unmount
    const safeEmit = (event, ...args) => {
      if (isMounted.value) {
        emit(event, ...args);
      }
    };

    // Phase 1 Refactored: Removed watcher - selectedMode now synced in onMounted only
    // Table will update automatically due to :key="allocation-table-${store.allocationMode}"

    // Watch for store allocationMode changes to sync selectedMode
    watch(() => store.allocationMode, newMode => {
      selectedMode.value = newMode;
    }, {
      immediate: true
    });

    // Watch for invoiceAccount changes to update allocations
    watch(() => props.invoiceAccount, async newInvoiceAccount => {
      // Skip if component not mounted or not initialized
      if (!isMounted.value || !isInitialized.value) return;

      // Normalize both old and new values to ID strings for comparison
      const newId = typeof newInvoiceAccount === 'string' ? newInvoiceAccount : newInvoiceAccount?.id || null;

      // Skip if the ID hasn't actually changed
      if (newId === lastInvoiceAccount.value) return;

      // Update the last processed ID
      lastInvoiceAccount.value = newId;

      // Use the master sync function to handle all scenarios
      await syncAllocationsWithInvoiceUser();
    }, {
      deep: true
    });

    // Watch for props allocations changes to sync with store
    // This handles external updates from parent component
    // ONLY sync props when in Percentage mode - other modes load from API
    watch(() => props.allocations, (newAllocations, oldAllocations) => {
      // Skip if component not mounted or still initializing
      // During initialization, we control the data flow
      if (!isMounted.value || !isInitialized.value) return;

      // CRITICAL: Only sync props allocations when in Percentage mode
      // Worked Hours/Amount modes load their data from API, not props
      if (store.allocationMode !== store.ALLOCATION_MODES.PERCENTAGE) {
        return;
      }

      // Skip if arrays are the same reference (avoid circular updates)
      if (newAllocations === oldAllocations) return;

      // DEFENSIVE: Don't clear store if parent sends empty/invalid data
      // Store is the source of truth once initialized
      if (!Array.isArray(newAllocations) || newAllocations.length === 0) {
        return;
      }

      // Convert from parent format to store format
      const normalizedAllocations = newAllocations.map(alloc => {
        if (alloc.account && typeof alloc.account === 'object') {
          return {
            accountId: alloc.account.id,
            accountName: alloc.account.name,
            amount: alloc.amount || 0,
            percentage: alloc.percentage || 0,
            workedHours: alloc.workedHours || 0,
            workedAmount: alloc.workedAmount || 0,
            main: alloc.main || false
          };
        }
        return alloc;
      });

      // Deep comparison: only update if data actually changed
      const currentIds = store.allocations.map(a => a.accountId).sort().join(',');
      const newIds = normalizedAllocations.map(a => a.accountId).sort().join(',');
      if (currentIds !== newIds) {
        store.updateAllocations([...normalizedAllocations]);
        savedPercentageAllocations.value = [...normalizedAllocations];
      }
    }, {
      deep: true
    });

    // Event handlers
    const handleModeChange = async newMode => {
      const currentMode = store.allocationMode;

      // If already on this mode, do nothing
      if (currentMode === newMode) {
        return;
      }

      // Store the previous mode to revert if cancelled
      previousMode.value = currentMode;

      // If switching FROM percentage TO read-only mode, save current allocations and show warning
      if (currentMode === store.ALLOCATION_MODES.PERCENTAGE && newMode !== store.ALLOCATION_MODES.PERCENTAGE) {
        // Save current percentage allocations before switching away
        savedPercentageAllocations.value = [...store.allocations];

        // Show warning if there are manual allocations
        if (store.allocations.some(a => a.percentage > 0)) {
          // First increment versions to prepare for re-render
          dialogVersion.value++;
          radioVersion.value++;

          // Wait for DOM update before opening dialog
          await nextTick();
          const confirmed = await modeChangeDialog.value?.open();
          if (!confirmed) {
            // User cancelled or clicked outside - revert to current mode
            selectedMode.value = currentMode;

            // Force another re-render to ensure radio buttons sync
            await nextTick();
            radioVersion.value++;
            return;
          }
        }
      }

      // Apply the mode change
      store.setAllocationMode(newMode);
      selectedMode.value = newMode;

      // When switching TO percentage mode, restore saved allocations if they exist
      if (newMode === store.ALLOCATION_MODES.PERCENTAGE) {
        // For saved entries, load allocations from API
        if (props.chargeId && props.chargeId !== 'new') {
          await store.loadAllocationsForMode(props.chargeId, newMode, props.users, props.fixedFeeAmount ?? null);
        } else {
          // For new/unsaved entries, restore from saved allocations or create default
          if (savedPercentageAllocations.value && savedPercentageAllocations.value.length > 0) {
            store.updateAllocations([...savedPercentageAllocations.value]);
          } else if (props.invoiceAccount && store.allocations.length === 0) {
            const invoiceAccountId = typeof props.invoiceAccount === 'string' ? props.invoiceAccount : props.invoiceAccount.id;
            const user = props.users.find(u => u.id === invoiceAccountId);
            if (user) {
              const chargeAmount = props.fixedFeeAmount || store.chargeAmount;
              const newAllocation = {
                accountId: user.id,
                accountName: user.name || user.displayName,
                amount: chargeAmount,
                percentage: 100,
                workedHours: 0,
                workedAmount: 0,
                main: true
              };
              store.updateAllocations([newAllocation]);
            }
          }
        }
      } else {
        // Trigger API call to fetch allocations for new mode (WORKED_HOURS or WORKED_AMOUNT)
        if (props.chargeId && props.chargeId !== 'new') {
          await store.loadAllocationsForMode(props.chargeId, newMode, props.users, props.fixedFeeAmount ?? null);
        } else {
          // For new charges, clear allocations when switching to Worked Hours/Amount modes
          // These modes show calculated data based on time entries, not manual allocations
          store.updateAllocations([]);
        }
      }

      // After mode change, sync invoice user with allocations
      await syncAllocationsWithInvoiceUser();
    };
    const handleAddUserFromSelect = user => {
      if (user && user.id) {
        store.addAllocation(user);

        // Save if in percentage mode
        if (store.allocationMode === store.ALLOCATION_MODES.PERCENTAGE) {
          savedPercentageAllocations.value = [...store.allocations];
        }

        // Emit updated allocations to parent in parent format
        const parentFormat = convertToParentFormat(store.allocations);
        emit('update:allocations', parentFormat);

        // Emit validation state
        emit('validation-changed', store.isAllocationValid);
        nextTick(() => {
          selectedUserForAdd.value = null;
          addUserPickerKey.value++;
        });
      }
    };

    // Convert store format (accountId, accountName) to parent format (account: { id, name })
    const convertToParentFormat = allocations => {
      if (!Array.isArray(allocations)) return [];
      return allocations.map(a => ({
        account: {
          id: a.accountId,
          name: a.accountName
        },
        amount: a.amount,
        percentage: a.percentage,
        main: a.main || false
      }));
    };

    /**
     * Master sync function: Ensures allocations and invoice user are perfectly in sync
     * Handles all scenarios: new/existing charges, with/without data, mode switches
     * SINGLE SOURCE OF TRUTH: Store allocations
     * This function is the only place where main flag logic should be handled
     */
    const syncAllocationsWithInvoiceUser = async () => {
      const isPercentageMode = store.allocationMode === store.ALLOCATION_MODES.PERCENTAGE;

      // Case 1: No invoice account - clear all main flags
      if (!props.invoiceAccount) {
        store.allocations.forEach(a => {
          a.main = false;
        });
        store.updateAllocations([...store.allocations]);
        if (isPercentageMode) {
          savedPercentageAllocations.value = [...store.allocations];
        }
        safeEmit('update:allocations', convertToParentFormat(store.allocations));
        return;
      }
      const invoiceAccountId = typeof props.invoiceAccount === 'string' ? props.invoiceAccount : props.invoiceAccount.id;
      const existingAllocation = store.allocations.find(a => a.accountId === invoiceAccountId);

      // Case 2: Invoice user exists in allocations - just update main flag
      if (existingAllocation) {
        store.allocations.forEach(a => {
          a.main = a.accountId === invoiceAccountId;
        });
        store.updateAllocations([...store.allocations]);
        if (isPercentageMode) {
          savedPercentageAllocations.value = [...store.allocations];
        }
        safeEmit('update:allocations', convertToParentFormat(store.allocations));
        return;
      }

      // Case 3: Invoice user NOT in allocations
      // Only add them if there are NO allocations yet (initial setup)
      // If allocations already exist, just clear main flags (user must manually add)
      const hasExistingAllocations = store.allocations.length > 0;
      if (!existingAllocation && isPercentageMode && !hasExistingAllocations) {
        // No allocations yet - auto-add invoice user for initial setup
        const user = props.users.find(u => u.id === invoiceAccountId);
        if (!user) {
          return;
        }
        const chargeAmount = props.fixedFeeAmount || store.chargeAmount;
        const newAllocation = {
          accountId: user.id,
          accountName: user.name || user.displayName,
          amount: chargeAmount,
          percentage: 100,
          workedHours: 0,
          workedAmount: 0,
          main: true
        };
        store.allocations.push(newAllocation);
        store.updateAllocations([...store.allocations]);
        savedPercentageAllocations.value = [...store.allocations];
        safeEmit('update:allocations', convertToParentFormat(store.allocations));
        return;
      }

      // Case 4: Invoice user not in allocations but allocations exist
      // Do NOT add - just clear main flags (user must manually add to table)
      store.allocations.forEach(a => {
        a.main = false;
      });
      store.updateAllocations([...store.allocations]);
      if (isPercentageMode) {
        savedPercentageAllocations.value = [...store.allocations];
      }
      safeEmit('update:allocations', convertToParentFormat(store.allocations));
    };
    const handleAllocationsUpdate = updated => {
      // The updated allocations come from AllocationTable in store format
      // (accountId, accountName, amount, percentage, etc.)
      // Update the store directly with the new allocations
      store.updateAllocations(updated);

      // Save if in percentage mode
      if (store.allocationMode === store.ALLOCATION_MODES.PERCENTAGE) {
        savedPercentageAllocations.value = [...updated];
      }

      // Emit back to parent in parent format (account: { id, name })
      const parentFormatAllocations = convertToParentFormat(store.allocations);
      emit('update:allocations', parentFormatAllocations);

      // Emit validation state to parent
      emit('validation-changed', store.isAllocationValid);
    };
    const handleRemoveAllocation = accountId => {
      // Check if the allocation being removed is the invoice user
      const allocationToRemove = store.allocations.find(a => a.accountId === accountId);
      const wasInvoiceUser = allocationToRemove?.main;
      store.removeAllocation(accountId);

      // If the removed allocation was the invoice user, clear the invoice account
      if (wasInvoiceUser) {
        emit('update:invoiceAccount', null);
      }

      // Save if in percentage mode
      if (store.allocationMode === store.ALLOCATION_MODES.PERCENTAGE) {
        savedPercentageAllocations.value = [...store.allocations];
      }

      // Emit updated allocations to parent in parent format
      emit('update:allocations', convertToParentFormat(store.allocations));
    };
    const handleAddAllocation = user => {
      store.addAllocation(user);

      // Save if in percentage mode
      if (store.allocationMode === store.ALLOCATION_MODES.PERCENTAGE) {
        savedPercentageAllocations.value = [...store.allocations];
      }

      // Emit updated allocations to parent in parent format
      const parentFormat = convertToParentFormat(store.allocations);
      emit('update:allocations', parentFormat);

      // Emit validation state
      emit('validation-changed', store.isAllocationValid);
    };
    const handleSplitEvenly = () => {
      const chargeAmount = props.fixedFeeAmount || store.chargeAmount;
      if (store.allocations.length === 0 || chargeAmount <= 0) return;
      const numAllocations = store.allocations.length;
      const distribution = distributeEvenly(chargeAmount, numAllocations);
      store.allocations.forEach((allocation, index) => {
        // Add remainder to first allocation
        if (index === 0) {
          allocation.amount = roundToDecimals(distribution.baseAmount + distribution.amountRemainder);
          allocation.percentage = roundToDecimals(distribution.basePercentage + distribution.percentageRemainder);
        } else {
          allocation.amount = distribution.baseAmount;
          allocation.percentage = distribution.basePercentage;
        }
      });
      store.updateAllocations([...store.allocations]);
      // Save if in percentage mode
      if (store.allocationMode === store.ALLOCATION_MODES.PERCENTAGE) {
        savedPercentageAllocations.value = [...store.allocations];
      }
      // Emit updated allocations to parent in parent format
      emit('update:allocations', convertToParentFormat(store.allocations));

      // Emit validation state
      emit('validation-changed', store.isAllocationValid);
    };
    const handleResetAllocation = () => {
      // Perform local reset instead of API call
      // Clear all current allocations
      store.updateAllocations([]);

      // If there's an invoice account selected, add that user with 100% allocation
      if (props.invoiceAccount) {
        const invoiceAccountId = typeof props.invoiceAccount === 'string' ? props.invoiceAccount : props.invoiceAccount.id;
        const user = props.users.find(u => u.id === invoiceAccountId);
        if (user) {
          const chargeAmount = props.fixedFeeAmount || store.chargeAmount;
          const newAllocation = {
            accountId: user.id,
            accountName: user.name || user.displayName,
            amount: chargeAmount,
            percentage: 100,
            workedHours: 0,
            workedAmount: 0,
            main: true
          };
          store.updateAllocations([newAllocation]);
        }
      }

      // Save if in percentage mode
      if (store.allocationMode === store.ALLOCATION_MODES.PERCENTAGE) {
        savedPercentageAllocations.value = [...store.allocations];
      }

      // Emit updated allocations to parent in parent format
      emit('update:allocations', convertToParentFormat(store.allocations));
    };
    const handleResetToDefaults = async () => {
      // Get company default mode for new charges
      let companyDefaultMode = null;
      if (props.chargeId == 'new') {
        const companyDefaultModeName = companySettings.value?.fixedFeeAllocationMode;
        if (companyDefaultModeName) {
          companyDefaultMode = store.ALLOCATION_MODE_FROM_API[companyDefaultModeName] ?? store.ALLOCATION_MODES.PERCENTAGE;
        }
      }

      // Pass the current fixed fee amount to recalculate allocations based on it
      await store.resetToDefaults(props.chargeId, props.users, companyDefaultMode, props.fixedFeeAmount);

      // Update selectedMode to match the reset mode
      selectedMode.value = store.allocationMode;

      // Sync invoice user with new allocations
      await syncAllocationsWithInvoiceUser();

      // Save if in percentage mode
      if (store.allocationMode === store.ALLOCATION_MODES.PERCENTAGE) {
        savedPercentageAllocations.value = [...store.allocations];
      }

      // Emit updated allocations to parent
      emit('update:allocations', convertToParentFormat(store.allocations));
    };
    const handleFieldUpdate = ({
      accountId,
      field,
      value
    }) => {
      // Additional validation or calculations can happen here
      // For now, the store handles it
    };
    const handleInvoiceUserChange = allocation => {
      // Handle null/undefined allocation (when unchecking)
      if (!allocation || !allocation.accountId) {
        // Clear all main flags
        const updatedAllocations = store.allocations.map(a => ({
          ...a,
          main: false
        }));
        store.updateAllocations(updatedAllocations);
        if (store.allocationMode === store.ALLOCATION_MODES.PERCENTAGE) {
          savedPercentageAllocations.value = [...updatedAllocations];
        }
        emit('update:allocations', convertToParentFormat(updatedAllocations));
        emit('validation-changed', store.isAllocationValid);
        emit('update:invoiceAccount', null);
        return;
      }

      // Update allocations with the new main flag
      const updatedAllocations = store.allocations.map(a => {
        if (a.accountId === allocation.accountId) {
          return {
            ...a,
            main: allocation.main
          };
        }
        return {
          ...a,
          main: false
        };
      });
      store.updateAllocations(updatedAllocations);

      // Save if in percentage mode
      if (store.allocationMode === store.ALLOCATION_MODES.PERCENTAGE) {
        savedPercentageAllocations.value = [...updatedAllocations];
      }

      // Emit allocations first to ensure parent has updated data
      emit('update:allocations', convertToParentFormat(updatedAllocations));

      // Emit validation state
      emit('validation-changed', store.isAllocationValid);

      // Then emit invoice account change
      // Use nextTick to ensure allocations are processed first
      nextTick(() => {
        if (allocation && allocation.main) {
          // Find the full user object to emit (ll-select needs this to display name)
          const user = props.users.find(u => u.id === allocation.accountId);
          if (user) {
            // Emit the full user object so ll-select can display the name
            emit('update:invoiceAccount', user);
          } else {
            // Fallback to ID if user not found
            emit('update:invoiceAccount', allocation.accountId);
          }
        } else {
          // If unchecked, clear the invoice account
          emit('update:invoiceAccount', null);
        }
      });
    };

    // AC10: Navigate to compensation settings page
    const goToCompensationSettings = () => {
      // Navigate to the report settings page where compensation settings are configured
      // Using the route service from props or window.location as fallback
      if (props.state && props.state.go) {
        props.state.go('settings.company.reports');
      } else {
        // Fallback: navigate using window location
        window.location.href = '/settings/company/reports';
      }
    };

    // Watch for validation changes and emit event
    watch(() => store.isAllocationValid, isValid => {
      emit('validation-changed', isValid);
    }, {
      immediate: true
    } // Emit initial validation state on mount
    );

    // Recalculate amounts when fixed fee changes in Percentage mode
    watch(() => props.fixedFeeAmount, (newAmount, oldAmount) => {
      if (store.allocationMode !== store.ALLOCATION_MODES.PERCENTAGE) return;
      const chargeAmount = newAmount || 0;
      if (!Array.isArray(store.allocations) || store.allocations.length === 0) return;

      // If any allocation has a percentage defined (including 0%), use percentage-based calculation
      // This ensures that when user sets "100% to user A, 0% to others", those 0% values are respected
      const anyHasPercentageDefined = store.allocations.some(a => a.percentage !== undefined && a.percentage !== null);
      if (anyHasPercentageDefined) {
        // Calculate amounts based on percentages
        let calculatedTotal = 0;
        const amounts = [];
        store.allocations.forEach(a => {
          const amount = roundToDecimals((Number(a.percentage) || 0) * chargeAmount / 100);
          amounts.push(amount);
          calculatedTotal += amount;
        });

        // Distribute any rounding remainder to the first non-zero allocation
        const remainder = roundToDecimals(chargeAmount - calculatedTotal);
        if (remainder !== 0) {
          const firstNonZeroIdx = amounts.findIndex(amt => amt > 0);
          if (firstNonZeroIdx >= 0) {
            amounts[firstNonZeroIdx] = roundToDecimals(amounts[firstNonZeroIdx] + remainder);
          } else {
            // If all amounts are zero, add remainder to first allocation
            amounts[0] = roundToDecimals(amounts[0] + remainder);
          }
        }

        // Apply calculated amounts
        store.allocations.forEach((a, idx) => {
          a.amount = amounts[idx];
        });
      } else {
        // Split evenly if no percentages have been set yet
        const numAllocations = store.allocations.length;
        const distribution = distributeEvenly(chargeAmount, numAllocations);
        store.allocations.forEach((allocation, index) => {
          if (index === 0) {
            allocation.amount = roundToDecimals(distribution.baseAmount + distribution.amountRemainder);
            // Store percentage with 8 decimal precision for accuracy
            allocation.percentage = roundPercentageForStorage(distribution.basePercentage + distribution.percentageRemainder);
          } else {
            allocation.amount = distribution.baseAmount;
            // Store percentage with 8 decimal precision for accuracy
            allocation.percentage = roundPercentageForStorage(distribution.basePercentage);
          }
        });
      }
      store.updateAllocations([...store.allocations]);
      if (store.allocationMode === store.ALLOCATION_MODES.PERCENTAGE) {
        savedPercentageAllocations.value = [...store.allocations];
      }
      emit('update:allocations', convertToParentFormat(store.allocations));
    }, {
      immediate: false
    });

    // Expose sync method so parent can trigger it on tab switch
    __expose({
      syncAllocationsWithInvoiceUser
    });
    return (_ctx, _cache) => {
      const _component_v_radio = _resolveComponent("v-radio");
      const _component_v_radio_group = _resolveComponent("v-radio-group");
      const _component_v_icon = _resolveComponent("v-icon");
      const _component_v_skeleton_loader = _resolveComponent("v-skeleton-loader");
      return _openBlock(), _createElementBlock("div", _hoisted_1, [_cache[8] || (_cache[8] = _createElementVNode("p", {
        class: "text-gray-700 text-sm"
      }, " Choose an allocation method and distribute the fixed fee among team members. This allocation divides the worked amount portion of the fee. ", -1)), !isServicesToResponsible.value ? (_openBlock(), _createElementBlock("div", _hoisted_2, [_createElementVNode("div", _hoisted_3, [_cache[1] || (_cache[1] = _createElementVNode("label", {
        class: "text-lg tracking-wide"
      }, "Fixed Fee Amount", -1)), _createElementVNode("div", _hoisted_4, [_createElementVNode("span", _hoisted_5, _toDisplayString(_unref(formatCurrency)(props.fixedFeeAmount)), 1)])]), _createElementVNode("div", _hoisted_6, [_createElementVNode("div", _hoisted_7, [_cache[2] || (_cache[2] = _createElementVNode("label", {
        class: "text-lg tracking-wide"
      }, "Allocation Scheme", -1)), _createVNode(LlTooltip, {
        text: "Restore to Company Defaults",
        location: "right",
        class: "position-absolute bottom-2"
      }, {
        activator: _withCtx(() => [_createVNode(LlSecondaryButton, {
          variant: "flat",
          icon: "mdi-cached",
          "aria-label": "Restore to Company Defaults",
          onClick: handleResetToDefaults,
          "data-cy": "allocation-restore-default",
          style: {
            "border-radius": "5px",
            "width": "40px",
            "height": "25px",
            "min-width": "40px"
          }
        })]),
        _: 1
      })]), (_openBlock(), _createBlock(_component_v_radio_group, {
        key: radioVersion.value,
        "model-value": selectedMode.value,
        "onUpdate:modelValue": handleModeChange,
        class: "flex gap-7",
        inline: "",
        "hide-details": "",
        color: "primary"
      }, {
        default: _withCtx(() => [_createVNode(_component_v_radio, {
          value: _unref(store).ALLOCATION_MODES.PERCENTAGE,
          label: "Percentage",
          "data-cy": "mode-percentage",
          class: "flex-shrink-0 pt-1.5 mb-0 radio-no-label-margin",
          "hide-details": ""
        }, null, 8, ["value"]), _createVNode(_component_v_radio, {
          value: _unref(store).ALLOCATION_MODES.WORKED_HOURS,
          label: "Worked Hours",
          "data-cy": "mode-worked-hours",
          class: "flex-shrink-0 pt-1.5 mb-0 radio-no-label-margin",
          "hide-details": ""
        }, null, 8, ["value"]), _createVNode(_component_v_radio, {
          value: _unref(store).ALLOCATION_MODES.WORKED_AMOUNT,
          label: "Worked Amount",
          "data-cy": "mode-worked-amount",
          class: "flex-shrink-0 pt-1.5 mb-0 radio-no-label-margin",
          "hide-details": ""
        }, null, 8, ["value"])]),
        _: 1
      }, 8, ["model-value"]))])])) : (_openBlock(), _createElementBlock("div", _hoisted_8, [_createElementVNode("div", _hoisted_9, [_cache[3] || (_cache[3] = _createElementVNode("label", {
        class: "text-lg tracking-wide"
      }, "Fixed Fee Amount", -1)), _createElementVNode("div", _hoisted_10, [_createElementVNode("span", _hoisted_11, _toDisplayString(_unref(formatCurrency)(props.fixedFeeAmount)), 1)])])])), stateMessage.value && !isServicesToResponsible.value ? (_openBlock(), _createElementBlock("div", _hoisted_12, [_createVNode(_component_v_icon, {
        size: "small",
        class: "mt-0.5 flex-shrink-0"
      }, {
        default: _withCtx(() => [...(_cache[4] || (_cache[4] = [_createTextVNode("mdi-information", -1)]))]),
        _: 1
      }), _createElementVNode("span", null, _toDisplayString(stateMessage.value), 1)])) : _createCommentVNode("", true), shouldShowTable.value ? (_openBlock(), _createElementBlock("div", _hoisted_13, [Array.isArray(_unref(allocations)) && _unref(allocations).length > 0 && !_unref(isReadOnly) ? (_openBlock(), _createBlock(LlSecondaryButton, {
        key: 0,
        class: "mt-[6px]",
        onClick: handleResetAllocation,
        "data-cy": "allocation-reset"
      }, {
        default: _withCtx(() => [...(_cache[5] || (_cache[5] = [_createTextVNode(" Reset ", -1)]))]),
        _: 1
      })) : _createCommentVNode("", true), Array.isArray(_unref(allocations)) && _unref(allocations).length > 0 && !_unref(isReadOnly) ? (_openBlock(), _createBlock(LlSecondaryButton, {
        key: 1,
        class: "mt-[6px]",
        onClick: handleSplitEvenly,
        "data-cy": "allocation-split-evenly"
      }, {
        default: _withCtx(() => [...(_cache[6] || (_cache[6] = [_createTextVNode(" Split Evenly ", -1)]))]),
        _: 1
      })) : _createCommentVNode("", true), canAddUsers.value ? (_openBlock(), _createElementBlock("div", _hoisted_14, [(_openBlock(), _createBlock(SelectUser, {
        key: addUserPickerKey.value,
        modelValue: selectedUserForAdd.value,
        "onUpdate:modelValue": [_cache[0] || (_cache[0] = $event => selectedUserForAdd.value = $event), handleAddUserFromSelect],
        placeholder: "Add User",
        density: "compact",
        variant: "outlined",
        "return-object": true,
        "custom-users": availableUsers.value,
        "data-cy": "allocation-add-user-select",
        label: ''
      }, null, 8, ["modelValue", "custom-users"]))])) : _createCommentVNode("", true)])) : _createCommentVNode("", true), !shouldShowTable.value && stateMessage.value && isServicesToResponsible.value ? (_openBlock(), _createElementBlock("div", {
        key: 4,
        class: "p-4 bg-blue-50 border border-blue-200 rounded",
        "data-cy": isServicesToResponsible.value ? 'allocation-services-to-responsible-card' : 'allocation-info-card'
      }, [_createElementVNode("div", _hoisted_16, [_createVNode(_component_v_icon, {
        color: "info",
        class: "mt-0.5 flex-shrink-0"
      }, {
        default: _withCtx(() => [...(_cache[7] || (_cache[7] = [_createTextVNode("mdi-information", -1)]))]),
        _: 1
      }), _createElementVNode("div", _hoisted_17, [_createElementVNode("p", _hoisted_18, [_createTextVNode(_toDisplayString(stateMessage.value) + " ", 1), isServicesToResponsible.value ? (_openBlock(), _createElementBlock("span", _hoisted_19, [_createElementVNode("a", {
        href: "javascript:void(0)",
        onClick: goToCompensationSettings,
        class: "font-semibold underline hover:no-underline"
      }, " Click here to view your compensation settings ")])) : _createCommentVNode("", true)])])])], 8, _hoisted_15)) : _createCommentVNode("", true), shouldShowTable.value && isServicesToResponsible.value && stateMessage.value ? (_openBlock(), _createElementBlock("div", _hoisted_20, [_createElementVNode("a", {
        href: "javascript:void(0)",
        onClick: goToCompensationSettings,
        class: "font-semibold underline hover:no-underline"
      }, " Click here to view your compensation settings ")])) : _createCommentVNode("", true), shouldShowTable.value ? (_openBlock(), _createElementBlock("div", _hoisted_21, [_unref(loading) ? (_openBlock(), _createElementBlock("div", _hoisted_22, [_createVNode(_component_v_skeleton_loader, {
        type: "table-row-divider@5",
        class: "w-full",
        elevation: "0"
      })])) : (_openBlock(), _createBlock(AllocationTable, {
        key: allocationTableKey.value,
        mode: _unref(store).allocationMode,
        allocations: allocationsArray.value,
        "charge-amount": props.fixedFeeAmount || _unref(store).chargeAmount,
        "is-read-only": _unref(store).isReadOnly,
        "can-add-users": false,
        "available-users": availableUsers.value,
        "validation-message": _unref(store).validationMessage,
        "onUpdate:allocations": handleAllocationsUpdate,
        "onRemove:allocation": handleRemoveAllocation,
        "onAdd:allocation": handleAddAllocation,
        "onSplit:evenly": handleSplitEvenly,
        "onReset:defaults": handleResetToDefaults,
        "onField:update": handleFieldUpdate,
        "onUpdate:invoiceUser": handleInvoiceUserChange
      }, null, 8, ["mode", "allocations", "charge-amount", "is-read-only", "available-users", "validation-message"]))])) : _createCommentVNode("", true), (_openBlock(), _createBlock(LlConfirmationDialog, {
        key: dialogVersion.value,
        ref_key: "modeChangeDialog",
        ref: modeChangeDialog,
        title: "Change Allocation Scheme",
        message: "Changing the allocation scheme will reset your manual percentage allocations. Do you want to continue?",
        confirmText: "Yes, Change",
        toggleText: "Cancel",
        "show-warning-icon": true
      }))]);
    };
  }
};